import { any, compose, defaultTo, equals, find, includes, pathOr, prop, propOr, sort } from 'ramda';
import { createSelector } from 'reselect';

import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import { getCodelistByName, getCodelistPropById, sortAlphabeticallyBy } from 'utils';

export const getRoot = propOr(EMPTY_VALUES.OBJECT, 'settings');
export const getActiveTab = createSelector(getRoot, prop('activeTab'));

// notificationSendingTab ------------------------------------------------------------------------------------------------------
export const getNotificationFilters = createSelector(getRoot, prop('notificationFilters'));

export const getOriginalEmailByNotificationSetIds = createSelector(
	getNotificationFilters,
	(state, notificationSetIds) => notificationSetIds,
	(notificationFilters, notificationSetIds) => {
		const filtersWithEmail = notificationFilters.filter((item) => item.email);
		const result = filtersWithEmail.find((item) => includes(item?.notificationSetId, notificationSetIds));
		return result?.email;
	},
);

// sorted by added description
export const getSortedTasksInfosLeadsByNotificationSetId = createSelector(
	(state) => getCodelistByName(codelists.taskTypes)(state),
	getNotificationFilters,
	(state, notificationSetId) => notificationSetId,
	(taskTypesCodelist, notificationFilters, notificationSetId) => {
		const notificationsById = notificationFilters.filter(
			(filter) => filter.notificationSetId === notificationSetId,
		);

		const notificationsWithDescription = notificationsById.map((item) => ({
			...item,
			description: getCodelistPropById(item.taskTypeId, taskTypesCodelist),
		}));

		const unsortedTasks = notificationsWithDescription.filter(
			(filter) => getCodelistPropById(filter.taskTypeId, taskTypesCodelist, 'informative') === 0,
		);
		const unsortedInformation = notificationsWithDescription.filter(
			(filter) => getCodelistPropById(filter.taskTypeId, taskTypesCodelist, 'informative') === 1,
		);
		const unsortedLeads = notificationsWithDescription.filter(
			(filter) => getCodelistPropById(filter.taskTypeId, taskTypesCodelist, 'informative') === 2,
		);

		const tasks = sort(sortAlphabeticallyBy('description'))(unsortedTasks);
		const information = sort(sortAlphabeticallyBy('description'))(unsortedInformation);
		const leads = sort(sortAlphabeticallyBy('description'))(unsortedLeads);

		return { tasks, information, leads };
	},
);

export const getMainSwitchValueByNotificationSetId = createSelector(
	getNotificationFilters,
	(state, notificationSetId) => notificationSetId,
	(filters, notificationSetId) => {
		const chosenNotificationSetIdFilters = filters.filter((item) => item.notificationSetId === notificationSetId);
		const arrayOfEnabledValues = chosenNotificationSetIdFilters.map((item) => propOr(false, 'enabled')(item));
		return any(equals(true))(arrayOfEnabledValues);
	},
);

export const getMainSwitchDisabledByNotificationSetId = createSelector(
	getNotificationFilters,
	(state, notificationSetId) => notificationSetId,
	(filters, notificationSetId) => {
		const chosenNotificationSetIdFilters = filters.filter((item) => item.notificationSetId === notificationSetId);
		const arrayOfEnabledValues = chosenNotificationSetIdFilters.map((item) => propOr(true, 'allowedChanges')(item));
		return any(equals(false))(arrayOfEnabledValues);
	},
);

export const getSubSwitchByNotifIdAndTaskType = createSelector(
	getNotificationFilters,
	(state, notificationSetId) => notificationSetId,
	(state, notificationSetId, taskTypeId) => taskTypeId,
	(filters, notificationSetId, taskTypeId) => {
		const chosenNotifSubFilter = filters.find(
			(item) => item.notificationSetId === notificationSetId && item.taskTypeId === taskTypeId,
		);
		return chosenNotifSubFilter;
	},
);

// infoCreationTab ------------------------------------------------------------------------------------------------------
export const getTaskFilters = createSelector(getRoot, prop('taskFilters'));

export const getTaskTypesAndLeadTypes = createSelector(getCodelistByName(codelists.taskTypes), (taskTypesCodelist) => {
	const unsortedTasks = taskTypesCodelist.filter((task) => task.informative === 0);
	const unsortedLeads = taskTypesCodelist.filter((task) => task.informative === 2);

	const tasks = sort(sortAlphabeticallyBy('description'))(unsortedTasks);
	const leads = sort(sortAlphabeticallyBy('description'))(unsortedLeads);

	return { tasks, leads };
});

export const getRelatedTaskTypesByTypeId = createSelector(
	getCodelistByName(codelists.taskSubtypes),
	(state, typeId) => typeId,
	(subtypesCodelist, typeId) => {
		const relatedSubtypes = subtypesCodelist.filter((subtype) =>
			includes(defaultTo(EMPTY_VALUES.STRING)(typeId).toString(), subtype.taskTypeIds),
		);

		const sortedRelatedSubtypes = sort(sortAlphabeticallyBy('description'))(relatedSubtypes);
		return sortedRelatedSubtypes;
	},
);

export const getFilterAccordingId = createSelector(
	getTaskFilters,
	(state, filterId) => filterId,
	(taskFilters, filterId) => {
		return taskFilters.find((filter) => filter.id === filterId);
	},
);

export const getSubfiltersOfFilter = createSelector(
	getTaskFilters,
	(state, taskTypeId) => taskTypeId,
	(taskFilters, taskTypeId) => {
		return compose(
			pathOr(null, ['subTypes']),
			find((filter) => filter.taskTypeId === taskTypeId),
		)(taskFilters);
	},
);

export const getCheckedFilterState = createSelector(
	getTaskFilters,
	(state, taskTypeId) => taskTypeId,
	(taskFilters, taskTypeId) => {
		return compose(
			pathOr(null, ['enabled']),
			find((filter) => filter.taskTypeId === taskTypeId),
		)(taskFilters);
	},
);
